import { useContext, useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import yup from 'utils/yup';
import { diContainerContext } from 'application/contexts/useDiContainer';
import { AgentCompanyListingsQuery } from 'interfaceAdapter/queries/AgentCompanyListings';
import { CompanyRegistrationProfileInfoQuery } from 'interfaceAdapter/queries/CompanyRegistrationProfileInfo';
import { IPublicCompany } from 'domain/entities/Company/Company';
import { IIndustry } from 'domain/entities/Industry/Industry';
import { useLoading } from 'application/contexts/useLoading';

const schema = yup
    .object({
        keyword: yup.string().nullable(),
        area: yup.array().of(yup.string()).nullable(),
        prefecture: yup.array().of(yup.string()).nullable(),
        number_of_employees: yup.array().of(yup.string()).nullable(),
        revenue_scale: yup.array().of(yup.string()).nullable(),
        industry: yup.array().of(yup.string()).nullable(),
        profit_level: yup.array().of(yup.string()).nullable(),
        net_assets_level: yup.array().of(yup.string()).nullable(),
    })
    .required();

type SearchForm = yup.InferType<typeof schema>;

const PAGE_SIZE = 10;

export const useCompanyList = () => {
    const diContainer = useContext(diContainerContext);
    const agentCompanyListingsQuery = diContainer.resolve(AgentCompanyListingsQuery);
    const companyRegistrationProfileInfoQuery = diContainer.resolve(CompanyRegistrationProfileInfoQuery);
    const { loading, setLoading } = useLoading();
    const [companies, setCompanies] = useState<IPublicCompany[]>([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [hasNextPage, setHasNextPage] = useState(false);

    // 検索条件
    const [areaChoices, setAreaChoices] = useState<string[][]>([]);
    const [prefectureChoices, setPrefectureChoices] = useState<string[][]>([]);
    const [employeeChoices, setEmployeeChoices] = useState<string[][]>([]);
    const [revenueChoices, setRevenueChoices] = useState<string[][]>([]);
    const [industryChoices, setIndustryChoices] = useState<IIndustry[]>([]);
    const [profitLevelChoices, setProfitLevelChoices] = useState<string[][]>([]);
    const [netAssetsLevelChoices, setNetAssetsLevelChoices] = useState<string[][]>([]);

    const { control, handleSubmit, getValues, watch, setValue } = useForm<SearchForm>({
        resolver: yupResolver(schema),
        mode: 'onChange',
        defaultValues: {
            keyword: '',
        },
    });

    useEffect(() => {
        setLoading(true);
        const fetchData = async () => {
            try {
                const offset = (currentPage - 1) * PAGE_SIZE;
                const resCompanies = await agentCompanyListingsQuery.fetchCompanies({
                    limit: PAGE_SIZE,
                    offset: offset,
                });
                setCompanies(resCompanies);
                // 取得したデータがPAGE_SIZE件の場合は次のページがある可能性がある
                setHasNextPage(resCompanies.length === PAGE_SIZE);
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [currentPage]);

    useEffect(() => {
        const fetchProfileInfo = async () => {
            const data = await companyRegistrationProfileInfoQuery.fetch();
            setAreaChoices(data.area_choices);
            setPrefectureChoices(data.prefecture_choices);
            setEmployeeChoices(data.employee_choices);
            setRevenueChoices(data.revenue_choices);
            setIndustryChoices(data.industries);
            setProfitLevelChoices(data.profit_level_choices);
            setNetAssetsLevelChoices(data.net_assets_level_choices);
        };
        fetchProfileInfo();
    }, []);

    // 検索実行
    const submit = handleSubmit(async (data) => {
        setLoading(true);
        setCurrentPage(1); // 検索時は1ページ目にリセット
        try {
            const offset = 0;
            const resCompanies = await agentCompanyListingsQuery.fetchCompanies({
                ...data,
                limit: PAGE_SIZE,
                offset: offset,
            });
            setCompanies(resCompanies);
            setHasNextPage(resCompanies.length === PAGE_SIZE);
        } finally {
            setLoading(false);
        }
    });

    const handlePreviousPage = () => {
        if (currentPage > 1) {
            setCurrentPage(currentPage - 1);
        }
    };

    const handleNextPage = () => {
        if (hasNextPage) {
            setCurrentPage(currentPage + 1);
        }
    };

    const handlePageChange = (page: number) => {
        if (page >= 1) {
            setCurrentPage(page);
        }
    };

    return {
        loading,
        companies,
        areaChoices,
        employeeChoices,
        revenueChoices,
        industryChoices,
        prefectureChoices,
        profitLevelChoices,
        netAssetsLevelChoices,
        getValues,
        setValue,
        watch,
        submit,
        control,
        agentCompanyListingsQuery,
        currentPage,
        hasNextPage,
        handlePreviousPage,
        handleNextPage,
        handlePageChange,
    };
};
